<template>
  <div class="advanced-copywriter">
    <!-- 标题 -->
    <div class="copywriter-section">
      <div class="section-header">
        <span class="label">标题 ({{ titleList.length }}/5)</span>
      </div>
      <div
        v-for="(_, i) in titleList"
        :key="'title-' + i"
        class="input-row"
      >
        <emoji-input
          placeholder="请输入标题"
          type="text"
          :value="titleList[i]"
          :index="i"
          @input="setTitle"
          class="flex-1"
        ></emoji-input>
        <div class="input-actions">
          <i
            class="el-icon-delete"
            @click="deleteItem('titleList', i)"
            v-if="titleList.length > 1"
          ></i>
        </div>
      </div>
      <div class="section-footer">
        <el-button
          type="text"
          size="small"
          @click="addItem('titleList')"
          :disabled="titleList.length >= 5"
        >
          <i class="el-icon-plus"></i>
          添加标题
        </el-button>
      </div>
    </div>

    <!-- 描述 -->
    <div class="copywriter-section">
      <div class="section-header">
        <span class="label">描述 ({{ descList.length }}/5)</span>
      </div>
      <div
        v-for="(_, i) in descList"
        :key="'desc-' + i"
        class="input-row"
      >
        <emoji-input
          placeholder="请输入描述"
          type="text"
          :value="descList[i]"
          :index="i"
          @input="setDesc"
          class="flex-1"
        ></emoji-input>
        <div class="input-actions">
          <i
            class="el-icon-delete"
            @click="deleteItem('descList', i)"
            v-if="descList.length > 1"
          ></i>
        </div>
      </div>
      <div class="section-footer">
        <el-button
          type="text"
          size="small"
          @click="addItem('descList')"
          :disabled="descList.length >= 5"
        >
          <i class="el-icon-plus"></i>
          添加描述
        </el-button>
      </div>
    </div>

    <!-- 正文 -->
    <message-editor v-model="messageList">
      <template #extra>
        <el-tooltip
          :disabled="!isMultipleSelected"
          content="选择多条数据时不支持AI推荐文案"
          placement="top"
        >
          <span>
            <el-tooltip
              effect="dark"
              placement="top"
            >
              <div slot="content">
                目前仅支持中文、英语、葡萄牙语、西班牙语 <br />
                官方说明文档：<a
                  href="https://www.facebook.com/business/help/497610041230617?locale=zh_CN"
                  target="_blank"
                  style="color: #409eff"
                  >https://www.facebook.com/business/help/497610041230617?locale=zh_CN</a
                >
              </div>
              <el-button
                type="text"
                size="small"
                @click="handleGenerateAiText"
                style="margin-left: 10px"
                :disabled="isGenerating || isMultipleSelected"
              >
                智能文案推荐
              </el-button>
            </el-tooltip>
          </span>
        </el-tooltip>
        <el-button
          v-if="aiGenerateStatus === 'generating'"
          type="text"
          size="small"
          style="margin-left: 10px"
          disabled
        >
          <i class="el-icon-loading"></i> ai文案生成中...
        </el-button>
        <el-button
          v-if="aiGenerateStatus === 'done'"
          type="text"
          size="small"
          style="margin-left: 10px"
          @click="openAiDialog"
        >
          查看文案
        </el-button>
      </template>
    </message-editor>

    <!-- AI文案推荐抽屉 -->
    <ai-text-drawer
      v-model="aiDialogVisible"
      :content-list="aiContentList"
      :loading="aiLoading"
      @confirm="handleAiConfirm"
    />
  </div>
</template>

<script>
import emojiInput from '@/views/adManagement/createAd/components/emojiInput';
import MessageEditor from './messageEditor.vue';
import AiTextDrawer from './aiTextDrawer.vue';
import { textGeneration, textGenerationQuery } from '@/api/creatAd';

export default {
  name: 'AdvancedCopywriter',
  components: {
    emojiInput,
    MessageEditor,
    AiTextDrawer,
  },
  props: {
    creative: {
      type: Object,
      default: () => ({}),
    },
    accountId: {
      type: [String, Number],
      default: '',
    },
    pageId: {
      type: [String, Number],
      default: '',
    },
    instagramActorId: {
      type: [String, Number],
      default: '',
    },
    // 选中的数据数量，用于控制AI推荐功能
    selectedCount: {
      type: Number,
      default: 1,
    },
  },
  data() {
    return {
      aiDialogVisible: false,
      aiLoading: false,
      aiContentList: [],
      aiGenerateStatus: 'idle',
      currentCreativeId: null,
      queryTimer: null,
    };
  },
  computed: {
    fbAssetFeedSpec() {
      return (
        this.creative.fbAssetFeedSpec || {
          titles: [''],
          mDescriptions: [''],
          mBodies: [''],
          imgList: [],
          videos: [],
          actionTypes: [],
          linkUrls: [''],
        }
      );
    },
    titleList: {
      get() {
        return this.fbAssetFeedSpec.titles?.length ? this.fbAssetFeedSpec.titles : [''];
      },
      set(val) {
        this.emitChange('titles', val);
      },
    },
    descList: {
      get() {
        return this.fbAssetFeedSpec.mDescriptions?.length ? this.fbAssetFeedSpec.mDescriptions : [''];
      },
      set(val) {
        this.emitChange('mDescriptions', val);
      },
    },
    messageList: {
      get() {
        return this.fbAssetFeedSpec.mBodies?.length ? this.fbAssetFeedSpec.mBodies : [''];
      },
      set(val) {
        this.emitChange('mBodies', val);
      },
    },
    isGenerating() {
      return this.aiGenerateStatus === 'generating';
    },
    // 是否选择了多条数据（两条及以上）
    isMultipleSelected() {
      return this.selectedCount >= 2;
    },
  },
  beforeDestroy() {
    if (this.queryTimer) {
      clearTimeout(this.queryTimer);
      this.queryTimer = null;
    }
  },
  methods: {
    setTitle(val, index) {
      const list = [...this.titleList];
      list.splice(index, 1, val);
      this.titleList = list;
    },
    setDesc(val, index) {
      const list = [...this.descList];
      list.splice(index, 1, val);
      this.descList = list;
    },
    addItem(type) {
      if (this[type].length >= 5) return;
      if (this[type].some((v) => !v || !v.trim())) {
        this.$message.warning('请先填写已有的空白项');
        return;
      }
      const list = [...this[type]];
      list.push('');
      this[type] = list;
    },
    deleteItem(type, index) {
      if (this[type].length <= 1) return;
      const list = [...this[type]];
      list.splice(index, 1);
      this[type] = list;
    },
    emitChange(key, val) {
      const currentSpec = this.creative.fbAssetFeedSpec || {
        titles: [''],
        mDescriptions: [''],
        mBodies: [''],
        imgList: [],
        videos: [],
        actionTypes: [],
        linkUrls: [''],
      };
      const newSpec = {
        ...currentSpec,
        [key]: val,
      };
      this.$emit('setCreativeMsg', { fbAssetFeedSpec: newSpec });
    },
    async handleGenerateAiText() {
      const params = {
        titles: this.titleList.filter((v) => v && v.trim()),
        mDescriptions: this.descList.filter((v) => v && v.trim()),
        mBodies: this.messageList.filter((v) => v && v.trim()),
        videos: this.fbAssetFeedSpec.videos,
        imgList: this.fbAssetFeedSpec.imgList,
        linkUrls: this.fbAssetFeedSpec.linkUrls || [],
        accountId: String(this.accountId || ''),
        media_type: this.creative.media_type || 'image',
        page_id: String(this.pageId || ''),
        instagram_actor_id: String(this.instagramActorId || ''),
        actionTypes: this.fbAssetFeedSpec.actionTypes || [],
      };

      try {
        this.aiGenerateStatus = 'generating';
        const res = await textGeneration(params);
        if (res.code === 0 && res.data) {
          this.currentCreativeId = res.data;

          this.pollQueryResult();
        } else {
          this.aiGenerateStatus = 'idle';
          //   this.$message.error(res.comment || '生成失败，请重试');
        }
      } catch (error) {
        this.aiGenerateStatus = 'idle';
        console.error('textGeneration error:', error);
        // this.$message.error('生成失败，请重试');
      }
    },
    async pollQueryResult() {
      if (!this.currentCreativeId) return;

      try {
        const res = await textGenerationQuery({ creativeId: this.currentCreativeId });
        if (res.code === 0 && res.data && res.data.length > 0) {
          this.aiContentList = res.data;
          this.aiGenerateStatus = 'done';
          this.$message.success('AI文案生成完成');
        } else if (res.code === 0) {
          this.queryTimer = setTimeout(() => {
            this.pollQueryResult();
          }, 3000);
        } else {
          this.aiGenerateStatus = 'idle';
          this.$message.error(res.comment || '生成失败，请重试');
        }
      } catch (error) {
        this.aiGenerateStatus = 'idle';
        console.error('textGenerationQuery error:', error);
        // this.queryTimer = setTimeout(() => {
        //   this.pollQueryResult();
        // }, 2000);
      }
    },
    openAiDialog() {
      this.aiDialogVisible = true;
    },
    handleAiConfirm(selectedContents) {
      const currentMessages = this.messageList.filter((v) => v && v.trim());
      const totalCount = currentMessages.length + selectedContents.length;
      if (totalCount > 5) {
        this.$message.warning('文案数量不能超过5个，请减少选择的文案数量');
        return;
      }
      let newMessages = [...currentMessages];
      for (const content of selectedContents) {
        if (!newMessages.includes(content)) {
          newMessages.push(content);
        }
      }
      this.messageList = newMessages.length ? newMessages : [''];
      // 保持 aiGenerateStatus 为 'done'，这样用户可以继续查看和选择文案
      // this.aiGenerateStatus = 'idle';
      this.$message.success('已添加选中的文案');
    },
  },
};
</script>

<style lang="scss" scoped>
.advanced-copywriter {
  .copywriter-section {
    margin-bottom: 16px;

    .section-header {
      display: flex;
      align-items: center;
      margin-bottom: 8px;

      .label {
        color: #333;
        font-weight: 500;
        margin-right: 12px;
      }
    }

    .input-row {
      display: flex;
      align-items: flex-start;
      margin-bottom: 8px;

      .flex-1 {
        flex: 1;
      }

      .input-actions {
        display: flex;
        align-items: center;
        margin-left: 8px;
        padding-top: 8px;

        i {
          cursor: pointer;
          font-size: 16px;
          color: #999;

          &:hover {
            color: #f56c6c;
          }
        }
      }
    }

    .section-footer {
      display: flex;
      align-items: center;
      margin-top: 4px;

      .el-button {
        padding-left: 0;
        padding-right: 0;
      }
    }
  }
}
</style>
